/**
 * useAnimatedSeries — Interpola series de datos para gráficos.
 *
 * QUÉ HACE: recibe un arreglo de objetos con campos numéricos y devuelve una
 * copia con esos campos animados: al montar suben desde 0 (entrada) y en cada
 * cambio de datos se transforman del valor mostrado al nuevo (subida/bajada).
 * Con `stagger` cada fila arranca unos ms después (barras escalonadas).
 * Es seguro ante el doble montaje de `React.StrictMode`: si la animación se
 * cancela sin completar, se reintenta; además un temporizador de seguridad fija
 * los valores finales aunque `requestAnimationFrame` esté en pausa (pestaña
 * oculta), de modo que el dato nunca queda en 0.
 * QUÉ INVOCA: `subscribeFrame` (bucle rAF compartido), `useReducedMotion`.
 * QUÉ EXPORTA: useAnimatedSeries, UseAnimatedSeriesOptions.
 * QUÉ NO DEBE HACER: no conoce Recharts ni el dominio; no anima campos no
 * numéricos (etiquetas, fechas, textos pasan intactos).
 */
import { useEffect, useMemo, useReducer, useRef } from "react";
import {
  lerp,
  MOTION,
  staggeredProgress,
  subscribeFrame,
} from "@/lib/animation/motion";
import { useReducedMotion } from "./useReducedMotion";

export interface UseAnimatedSeriesOptions {
  /** Duración de la transición en ms (por defecto `MOTION.series`). */
  duration?: number;
  /** Retardo por fila en ms para entradas escalonadas (por defecto 0). */
  stagger?: number;
  /** Desactiva la animación (por defecto `true`). */
  enabled?: boolean;
  /**
   * Claves numéricas a animar. Si se omite, se detectan todas las numéricas
   * del primer elemento.
   */
  keys?: readonly string[];
}

const readNumericKeys = <T extends object>(sample: T | undefined): string[] => {
  if (!sample) return [];
  return Object.keys(sample).filter(
    (key) => typeof (sample as Record<string, unknown>)[key] === "number",
  );
};

const readMatrix = <T extends object>(
  data: T[],
  keys: readonly string[],
): number[][] =>
  data.map((item) =>
    keys.map((key) => {
      const value = (item as Record<string, unknown>)[key];
      return typeof value === "number" && Number.isFinite(value) ? value : 0;
    }),
  );

const buildSignature = <T extends object>(
  data: T[],
  keys: readonly string[],
): string =>
  `${keys.join(",")}#${data.length}#${readMatrix(data, keys)
    .map((row) => row.join(","))
    .join("|")}`;

export function useAnimatedSeries<T extends object>(
  data: T[],
  options: UseAnimatedSeriesOptions = {},
): T[] {
  const { duration = MOTION.series, stagger = 0, enabled = true } = options;
  const reducedMotion = useReducedMotion();
  const animate = enabled && !reducedMotion && data.length > 0;

  const keys = useMemo(
    () =>
      options.keys && options.keys.length > 0
        ? [...options.keys]
        : readNumericKeys(data[0]),
    [data, options.keys],
  );

  const signature = useMemo(() => buildSignature(data, keys), [data, keys]);

  const displayRef = useRef<number[][] | null>(null);
  /** Firma de la animación ya iniciada y de la ya completada (o `null`). */
  const signatureRef = useRef<string | null>(null);
  const completedRef = useRef<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const settleRef = useRef<number | null>(null);
  const [, forceRender] = useReducer((count: number) => count + 1, 0);
  /**
   * Últimos datos y claves. La animación lee de aquí para que su efecto dependa
   * solo de la firma (string): si dependiera de la identidad de `targets`
   * —nueva en cada render— se reiniciaría en cada frame y nunca terminaría.
   */
  const sourceRef = useRef({ data, keys });

  useEffect(() => {
    sourceRef.current = { data, keys };
  });

  if (
    animate &&
    (displayRef.current === null ||
      displayRef.current.length !== data.length ||
      (displayRef.current[0]?.length ?? 0) !== keys.length)
  ) {
    displayRef.current = data.map(() => keys.map(() => 0));
  }

  useEffect(() => {
    const stop = (): void => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
      if (settleRef.current !== null) {
        window.clearTimeout(settleRef.current);
        settleRef.current = null;
      }
    };

    if (!animate) {
      stop();
      return;
    }
    /**
     * Se anima si la firma cambió o si la animación anterior no llegó a
     * completarse (p. ej. StrictMode canceló el primer montaje).
     */
    if (
      signatureRef.current === signature &&
      completedRef.current === signature
    ) {
      return;
    }
    signatureRef.current = signature;
    completedRef.current = null;
    stop();

    const targets = readMatrix(sourceRef.current.data, sourceRef.current.keys);
    const current = displayRef.current;
    const from =
      current && current.length === targets.length
        ? current
        : targets.map((row) => row.map(() => 0));
    const total = duration + Math.max(targets.length - 1, 0) * stagger;
    const startTime = performance.now();

    const finish = (): void => {
      displayRef.current = targets;
      completedRef.current = signature;
      stop();
      forceRender();
    };

    unsubscribeRef.current = subscribeFrame((now) => {
      const elapsed = now - startTime;
      if (elapsed >= total) {
        finish();
        return;
      }
      displayRef.current = targets.map((row, rowIndex) =>
        row.map((target, columnIndex) =>
          lerp(
            from[rowIndex]?.[columnIndex] ?? 0,
            target,
            staggeredProgress(elapsed, rowIndex, duration, stagger),
          ),
        ),
      );
      forceRender();
    });

    /** Red de seguridad: fija el dato final si el rAF no llega a dispararse. */
    settleRef.current = window.setTimeout(finish, total + 200);
  }, [animate, signature, duration, stagger, forceRender]);

  useEffect(() => {
    return () => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
      if (settleRef.current !== null) {
        window.clearTimeout(settleRef.current);
        settleRef.current = null;
      }
    };
  }, []);

  const display = displayRef.current;
  if (!animate || !display || display.length !== data.length) return data;

  return data.map((item, rowIndex) => {
    const row = display[rowIndex];
    if (!row || row.length !== keys.length) return item;
    const next = { ...item };
    keys.forEach((key, columnIndex) => {
      Object.assign(next, { [key]: row[columnIndex] });
    });
    return next;
  });
}
